import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import type { PickerRowsByUidArgs, UidsLookup } from '../canonicalPicker.types'
import { allRowsOf, BaseQuery } from './paging'
import {
  canListStoreUids,
  chunksOf,
  groupsHoldingUrl,
  rowsByKeyUrls,
  storeUidsUrls,
  uidLookupUrl,
  UIDS_PER_REQUEST,
} from './query'
import { isUidGroupRow, pageOf, soldUidsOf, uidsLookupOf } from './rows'

type Result = { data: UidsLookup } | { error: FetchBaseQueryError }

/**
 * The groups that hold searched uids, as their group or a store UID. A store
 * UID counts only when the details source lists it, which nexus trims to the
 * locations the merchant can see; the groups' own store UID lists are
 * chain-wide, so they only say which group a found store UID sits in. A
 * source with no details source falls back on those lists.
 */
const uidsLookup = async (
  { source, uids }: PickerRowsByUidArgs,
  baseQuery: BaseQuery
): Promise<Result> => {
  if (!canListStoreUids(source)) {
    const result = await allRowsOf(
      chunksOf(uids, UIDS_PER_REQUEST).map(
        chunk => page => uidLookupUrl(source, chunk, page)
      ),
      baseQuery
    )
    if ('error' in result) return result
    const found = pageOf(result.data, source, 'uidGroup', true).rows
    return { data: uidsLookupOf(uids, found.filter(isUidGroupRow)) }
  }

  const [keyed, sold] = await Promise.all([
    allRowsOf(
      rowsByKeyUrls({ source, kind: 'uidGroup', keys: uids, scope: null }),
      baseQuery
    ),
    allRowsOf(storeUidsUrls(source, null, uids), baseQuery),
  ])
  if ('error' in keyed) return keyed
  if ('error' in sold) return sold
  const soldPairs = soldUidsOf(sold.data)
  // A pair is provider:uid; provider names hold no colon
  const soldUidSet = new Set(
    Array.from(soldPairs, pair => pair.slice(pair.indexOf(':') + 1))
  )
  const soldUids = uids.filter(uid => soldUidSet.has(uid))

  const holding = await allRowsOf(
    chunksOf(soldUids, UIDS_PER_REQUEST).map(
      chunk => page => groupsHoldingUrl(source, chunk, page)
    ),
    baseQuery
  )
  if ('error' in holding) return holding
  const found = [
    ...pageOf(keyed.data, source, 'uidGroup', true).rows,
    ...pageOf(holding.data, source, 'uidGroup', true).rows,
  ].filter(isUidGroupRow)
  return {
    data: uidsLookupOf(uids, found, (provider, uid) =>
      soldPairs.has(`${provider}:${uid}`)
    ),
  }
}

export default uidsLookup
